import sortBy from 'lodash/sortBy';

class ProductGroups {
  /**
   * Initialize a new instance of ProductGroups class.
   * @param data
   * @param options
   */
  constructor(data, options = {}) {
    this.data = data;
    if (!data || !data.variants || !data.variantAttributesDirectory) {
      throw new Error('Product data is invalid.');
    }

    this.options = Object.assign(this.getDefaultOptions(), options);
    this.productsMap = this.getProductsMap();
    this.images = this.getPrimaryImages();
  }

  /**
   * Get default options.
   */
  getDefaultOptions() {
    return {
      isSkipArchiveProducts: true,
    };
  }

  /**
   * Get a list of primary images.
   * @return {{}}
   */
  getPrimaryImages() {
    const images = {};
    this.data?.assetImageList?.forEach((option) => {
      const primary = option.value.reduce((prev, curr) => (prev.weight < curr.weight ? prev : curr));
      images[option.key] = primary.assetId || '';
    });
    return images;
  }

  /**
   * Initialize object with a list of variants where variant's code is a key.
   * @return {{}}
   */
  getProductsMap() {
    const map = {};
    this.data.variants.forEach((variant) => {
      map[variant.code] = Object.assign(
        {
          skuQtyInSavedList: '',
          skuQtyInCart: '',
        },
        variant
      );
    });

    return map;
  }

  /**
   * Get general data which is needed for rendering group.
   * @return {{code: *, name: *, productType: *, marketingModelYear: *}}
   */
  getGroupData() {
    return {
      code: this.data.code,
      productType: this.data.productType,
      name: this.data.name,
      marketingModelYear: this.data.marketingModelYear,
      packageQty: parseInt(this.data.packageQty),
      totalShipmentNumber: this.data.totalShipmentNumber,
      isPreSeasonImmediateOrder: this.data.isPreseasonImmediateOrder,
    };
  }

  getVariantsForGroup(option, group) {
    let variants = this.data?.variants
      .filter((v) => v.variantAttributes[group.code] === option.value)
      .map((v) => {
        const categorization = [];

        this.data.variantAttributesDirectory?.forEach((childGroup, index) => {
          // Ignore first element
          if (index === 0) return;

          const childValue = v.variantAttributes[childGroup.code];
          const childAttribute = childGroup.options.find((child) => child.value === childValue);

          categorization.push({
            name: childGroup.displayName,
            value: childAttribute?.displayName,
            sequence: childAttribute?.sequence,
            groupValue: childAttribute?.value,
          });
        });

        return {
          skuQtyInCart: '',
          ...v,
          categorization,
        };
      });
    variants = sortBy(variants, (item) => item?.categorization[0]?.sequence);

    if (variants[0].categorization.length > 1) {
      let level = 1;
      //incase if we have multiple categorizations
      for (let i = 0; i < variants[0].categorization.length - 1; i++) {
        variants = this.makeGroupAndSortArrBasedOnKey(variants, i, level);
        level++;
      }
    }
    return variants;
  }

  makeGroupAndSortArrBasedOnKey(list, index, level) {
    let sortedList = [];
    let currentValue = list[0]?.categorization[index]?.groupValue;
    let currentGroupList = [list[0]];
    for (let i = 1; i < list.length; i++) {
      const groupValue = list[i]?.categorization[index]?.groupValue;
      if (currentValue === groupValue) {
        currentGroupList.push(list[i]);
      } else {
        currentGroupList = sortBy(currentGroupList, (item) => item?.categorization[level]?.sequence);
        sortedList = [...sortedList, ...currentGroupList];
        currentGroupList = [list[i]];
        currentValue = groupValue;
      }
    }
    if (currentGroupList.length !== 0) {
      currentGroupList = sortBy(currentGroupList, (item) => item?.categorization[level]?.sequence);
      sortedList = [...sortedList, ...currentGroupList];
    }
    return sortedList;
  }

  getProductGroups() {
    const group = this.data.variantAttributesDirectory[0];
    return group.options.map((option) => this.getProductGroupForColor(group, option));
  }

  getProductGroupForColor(group, option) {
    const sizes = this.getVariantsForGroup(option, group);
    const modelYearCurrent = this.getCurrentModelYear(sizes);
    const productGroup = {
      group: {
        meta: {
          code: group.code,
          type: group.type,
          options: {
            displayName: group.displayName,
            specFinishTranslated: group.specFinishTranslated,
          },
        },
        value: {
          children: [],
          code: group.code,
          value: option.value,
          options: {
            colorSwatchName: option.colorSwatchName,
            displayName: option.displayName,
            imageOption: option.imageOption,
            productCode: option.code,
            specFinishTranslated: option.specFinishTranslated,
          },
        },
        image: this.images?.[option.colorSwatchName],
      },
      modelYearCurrent,
      products: sizes,
      ...this.getGroupData(),
    };
    return productGroup;
  }

  getCurrentModelYear(items) {
    const variantYearList = [];
    let currentModelYear = '';

    const getModelYear = (products) => {
      products.forEach((product) => {
        variantYearList.push(product.marketingModelYear);
      });
      variantYearList.sort((a, b) => a - b);
      let uniqueYear = [...new Set(variantYearList)];

      if (uniqueYear.length > 1) {
        currentModelYear = uniqueYear[uniqueYear.length - 1];
        return currentModelYear;
      } else {
        currentModelYear = uniqueYear[0];
        return currentModelYear;
      }
    };
    return getModelYear(items);
  }
}

export default ProductGroups;
